import { coversBasicLatin } from './fontCatalog'
import type { FontCatalogStyle, FontCatalogSummary } from '../../types/fontCatalog'

export type FontStyleKey = 'regular' | 'bold' | 'italic' | 'boldItalic'

export interface FontFamilyTraits {
  styles: Array<{ min: number, max: number, style: FontCatalogStyle }>
  regular: boolean
  bold: boolean
  italic: boolean
  boldItalic: boolean
  monospace: boolean
  variable: boolean
  weightRange: [number, number]
  styleCount: number
  license: string
}

const traitsCache = new WeakMap<FontCatalogSummary, FontFamilyTraits>()

const LICENSE_ALIASES: Record<string, string> = {
  'OFL-1.1': 'OFL',
  APACHE2: 'Apache-2.0',
}

function normalizeFontLicense(license: string): string {
  return LICENSE_ALIASES[license] ?? license
}

function parseWeight(weight: string): { min: number, max: number } {
  const [min, max = min] = weight.trim().split(/\s+/).map(Number)
  return { min, max }
}

/**
 * Normalized filter data for a catalog family. Uses the catalog's precomputed
 * metadata (every index.json entry has it) and derives it from the file list
 * only for installed families saved before that metadata existed.
 */
export function getFontFamilyTraits(family: FontCatalogSummary): FontFamilyTraits {
  const cached = traitsCache.get(family)
  if (cached) return cached

  const rawStyles = family.styles ?? [
    ...new Map((family.files ?? []).map((file) => [`${file.weight}|${file.style}`, file])).values(),
  ]
  const styles = rawStyles
    .map((item) => ({ ...parseWeight(item.weight), style: item.style }))
    .filter((item) => Number.isFinite(item.min) && Number.isFinite(item.max))
  const covers = (style: FontCatalogStyle, weight: number) =>
    styles.some((item) => item.style === style && item.min <= weight && weight <= item.max)
  const derivedRange: [number, number] = styles.length
    ? [Math.min(...styles.map((item) => item.min)), Math.max(...styles.map((item) => item.max))]
    : [400, 400]

  const traits: FontFamilyTraits = {
    styles,
    regular: family.hasRegular ?? covers('normal', 400),
    bold: family.hasBold ?? covers('normal', 700),
    italic: family.hasItalic ?? covers('italic', 400),
    boldItalic: family.hasBoldItalic ?? covers('italic', 700),
    monospace: family.isMonospace ?? family.category === 'monospace',
    variable: styles.some((item) => item.min !== item.max),
    weightRange: family.weightRange ?? derivedRange,
    styleCount: family.styleCount ?? styles.length,
    license: normalizeFontLicense(family.license),
  }
  traitsCache.set(family, traits)
  return traits
}

/** True when any upright or italic face (static or variable) provides the weight. */
export function familyHasWeight(traits: FontFamilyTraits, weight: number): boolean {
  return traits.styles.some((item) => item.min <= weight && weight <= item.max)
}

const SCRIPT_LABEL_OVERRIDES: Record<string, string> = {
  'latin-ext': 'Latin Extended',
  'cyrillic-ext': 'Cyrillic Extended',
  'greek-ext': 'Greek Extended',
  symbols2: 'Symbols 2',
  nko: 'N’Ko',
}

export function formatScriptName(subset: string): string {
  return SCRIPT_LABEL_OVERRIDES[subset]
    ?? subset.split('-').map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')
}

/** Subsets that hold Latin letters or no letters at all, rather than a script of their own. */
const NON_SCRIPT_SUBSETS = new Set(['latin', 'latin-ext', 'vietnamese', 'symbols', 'symbols2', 'math', 'emoji', 'music', 'menu'])

export type FontLetterCoverage =
  | { kind: 'english' }
  /** No English letters; writes another script, such as Korean. */
  | { kind: 'other-script', scripts: string[] }
  /** No letters in any script: symbols, icons or special characters only. */
  | { kind: 'symbols' }

/**
 * What a font can write. Uses the catalog's hasBasicLatin flag, else the
 * family's files, else (older catalogs, no files) whether it lists Latin.
 */
export function getFontLetterCoverage(family: FontCatalogSummary): FontLetterCoverage {
  const english = family.hasBasicLatin
    ?? (family.files ? family.files.some(coversBasicLatin) : family.subsets.includes('latin'))
  if (english) return { kind: 'english' }
  const scripts = family.subsets.filter((subset) => !NON_SCRIPT_SUBSETS.has(subset)).map(formatScriptName)
  return scripts.length ? { kind: 'other-script', scripts } : { kind: 'symbols' }
}

function listNames(names: string[]): string {
  if (names.length <= 2) return names.join(' and ')
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/** A short label for lists and cards, or null for fonts with English letters. */
export function letterCoverageLabel(coverage: FontLetterCoverage): string | null {
  if (coverage.kind === 'other-script') return `No English letters · ${coverage.scripts.slice(0, 2).join(', ')}${coverage.scripts.length > 2 ? '…' : ''}`
  if (coverage.kind === 'symbols') return 'Symbols only, no letters'
  return null
}

/** The sentence shown when such a font is chosen, or null for fonts with English letters. */
export function letterCoverageNotice(name: string, coverage: FontLetterCoverage): string | null {
  if (coverage.kind === 'other-script') return `${name} has no English letters. It’s for writing in ${listNames(coverage.scripts)}.`
  if (coverage.kind === 'symbols') return `${name} has only symbols and special characters, no letters.`
  return null
}
